import AdministratorPanel from "@components/AdministratorPanel";
import AppBar from "@components/AppBar";
import InteractiveAvatar from "@components/InteractiveAvatar";
import LayoutDefault from "@layouts/LayoutDefault";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import RecordVoiceOverIcon from "@mui/icons-material/RecordVoiceOver";
import { Box } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useState } from "react";
import Sidebar from "@components/Sidebar/Sidebar";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface TabConfig {
  value: number;
  labelKey: string;
  icon: React.ReactElement;
  component: React.ReactNode;
  hasAccess: boolean;
}

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2, height: "100%" }}>{children}</Box>}
    </div>
  );
};

const MainView = () => {
  const { keycloak } = useKeycloak();
  const hasAdminAccess = keycloak.hasRealmRole("cesif_ui_administrator");

  // Define all possible tabs and their access conditions.
  const allTabs = [
    {
      labelKey: "administration.title",
      icon: <ManageAccountsIcon />,
      component: <AdministratorPanel />,
      hasAccess: hasAdminAccess
    },
    {
      labelKey: "home.title1",
      icon: <RecordVoiceOverIcon />,
      component: <InteractiveAvatar />,
      hasAccess: true
    }
  ];

  // Filter tabs based on access rights and assign a sequential value default tab is always at index 0.
  const tabs: TabConfig[] = allTabs
    .filter(tab => tab.hasAccess)
    .map((tab, index) => ({ ...tab, value: index }));

  // The default tab is always the first one in the filtered list.
  const [value, setValue] = useState(0);

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  return (
    <LayoutDefault title="Lighthouse">
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "100%",
          backgroundColor: "#f6f6f6",
          backgroundSize: "cover",
          backgroundPosition: "center"
        }}
      >

        {/* Sidebar */}
        {hasAdminAccess && (
          <Sidebar value={value} handleChange={handleChange} tabs={tabs} />
        )}

        {/* Content */}
        <Box width={"100%"}>
          <AppBar />

          <Box
            sx={{
              flexGrow: 1,
              overflow: "auto",
              alignSelf: "center",
              height: "calc(100% - 130px)"
            }}
          >
            {tabs.map((tab, index) => (
              tab.hasAccess && (
                <CustomTabPanel key={index} value={value} index={tab.value}>
                  {tab.component}
                </CustomTabPanel>
              )
            ))}
          </Box>
        </Box>
      </Box>
    </LayoutDefault>
  );
};

export default MainView;
